import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { BiInfoCircle, BiTime, BiCalendar } from 'react-icons/bi';
import ListeDeroulante from '../../UI/ListeDeroulante/ListeDeroulante';
import S from './FrequencyInput.module.scss';

const FREQUENCY_OPTIONS = [
  { value: 'once', label: 'Une seule execution' },
  { value: 'hourly', label: 'Horaire' },
  { value: 'daily', label: 'Quotidienne' },
  { value: 'weekly', label: 'Hebdomadaire' },
  { value: 'days_of_week', label: 'Jours de la semaine' },
  { value: 'monthly', label: 'Mensuelle' },
  { value: 'yearly', label: 'Annuelle' },
  { value: 'custom', label: 'Intervalle personnalisé' },
];

const DAYS_OF_WEEK = [
  { value: '1', label: 'Lundi' },
  { value: '2', label: 'Mardi' },
  { value: '3', label: 'Mercredi' },
  { value: '4', label: 'Jeudi' },
  { value: '5', label: 'Vendredi' },
  { value: '6', label: 'Samedi' },
  { value: '0', label: 'Dimanche' },
];

const INTERVAL_FIELDS = [
  { key: 'interval_years', label: 'Années', width: '25%' },
  { key: 'interval_months', label: 'Mois', width: '25%' },
  { key: 'interval_weeks', label: 'Semaines', width: '25%' },
  { key: 'interval_days', label: 'Jours', width: '25%' },
];

const TIME_FIELDS = [
  { key: 'interval_hours', label: 'Heures', width: '33%' },
  { key: 'interval_minutes', label: 'Minutes', width: '33%' },
  { key: 'interval_seconds', label: 'Secondes', width: '33%' },
];

/**
 * Composant pour la configuration de la fréquence d'exécution d'une tâche planifiée
 * Gère les fréquences prédéfinies et les intervalles personnalisés
 */
const FrequencyInput = ({
  mode,
  frequence,
  intervalYears,
  intervalMonths,
  intervalWeeks,
  intervalDays,
  intervalHours,
  intervalMinutes,
  intervalSeconds,
  frequenceDate,
  frequenceTime,
  endDate,
  endTime,
  daysOfWeek,
  onChange,
}) => {
  // Memoize interval values map
  const intervalValues = useMemo(
    () => ({
      interval_years: intervalYears,
      interval_months: intervalMonths,
      interval_weeks: intervalWeeks,
      interval_days: intervalDays,
      interval_hours: intervalHours,
      interval_minutes: intervalMinutes,
      interval_seconds: intervalSeconds,
    }),
    [
      intervalYears,
      intervalMonths,
      intervalWeeks,
      intervalDays,
      intervalHours,
      intervalMinutes,
      intervalSeconds,
    ]
  );

  // Memoized handler for number inputs
  const handleNumberChange = useCallback(
    (field) => (e) => {
      const value = Math.max(0, parseInt(e.target.value || '0', 10));
      onChange(field, value);
    },
    [onChange]
  );

  // Memoized handler for date/time inputs
  const handleDateTimeChange = useCallback(
    (field) => (e) => {
      onChange(field, e.target.value);
    },
    [onChange]
  );

  const handleFrequenceChange = useCallback(
    (value) => {
      onChange('frequence', value);
    },
    [onChange]
  );

  const handleDayOfWeekChange = useCallback(
    (dayValue) => (e) => {
      const currentDays = daysOfWeek
        ? daysOfWeek.split(',').filter((d) => d)
        : [];
      let newDays;

      if (e.target.checked) {
        // Ajouter le jour
        newDays = [...currentDays, dayValue];
      } else {
        // Retirer le jour
        newDays = currentDays.filter((d) => d !== dayValue);
      }

      onChange('days_of_week', newDays.join(','));
    },
    [daysOfWeek, onChange]
  );

  if (mode !== 'scheduled') {
    return null;
  }

  return (
    <div className={S.frequencyContainer}>
      <div className={S.frequencyContent}>
        {/* Sélection de la fréquence */}
        <div className={S.frequencyGroup}>
          <div className={S.groupHeader}>
            <BiTime size={16} />
            <span className={S.groupLabel}>Type de fréquence</span>
          </div>
          <div className={S.inputGroup}>
            <label className={S.label}>Fréquence</label>
            <ListeDeroulante
              value={frequence}
              onChange={handleFrequenceChange}
              options={FREQUENCY_OPTIONS}
            />
          </div>
        </div>

        {/* Jours de la semaine */}
        {frequence === 'days_of_week' && (
          <div className={S.frequencyGroup}>
            <div className={S.groupHeader}>
              <BiCalendar size={16} />
              <span className={S.groupLabel}>Sélectionner les jours</span>
            </div>

            <div className={S.daysOfWeekContainer}>
              {DAYS_OF_WEEK.map(({ value, label }) => {
                const selectedDays = daysOfWeek
                  ? daysOfWeek.split(',').filter((d) => d)
                  : [];
                return (
                  <label key={value} className={S.checkboxLabel}>
                    <input
                      type="checkbox"
                      value={value}
                      checked={selectedDays.includes(value)}
                      onChange={handleDayOfWeekChange(value)}
                      className={S.checkbox}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>

            <div className={S.timeInputs}>
              <div className={S.inputGroup}>
                <label className={S.label}>Heure d'exécution</label>
                <input
                  type="time"
                  value={frequenceTime}
                  onChange={handleDateTimeChange('frequence_time')}
                  className={S.timeInput}
                />
              </div>
            </div>
          </div>
        )}

        {/* Intervalle personnalisé */}
        {frequence === 'custom' && (
          <div className={S.frequencyGroup}>
            <div className={S.groupHeader}>
              <BiTime size={16} />
              <span className={S.groupLabel}>Intervalle personnalisé</span>
            </div>

            <div className={S.intervalInputs}>
              {INTERVAL_FIELDS.map(({ key, label }) => (
                <div key={key} className={S.inputGroup}>
                  <label className={S.label}>{label}</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={intervalValues[key]}
                    onChange={handleNumberChange(key)}
                    className={S.numberInput}
                  />
                </div>
              ))}
            </div>

            <div className={S.timeInputs}>
              {TIME_FIELDS.map(({ key, label }) => (
                <div key={key} className={S.inputGroup}>
                  <label className={S.label}>{label}</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={intervalValues[key]}
                    onChange={handleNumberChange(key)}
                    className={S.numberInput}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date et heure de début */}
        <div className={S.frequencyGroup}>
          <div className={S.groupHeader}>
            <BiCalendar size={16} />
            <span className={S.groupLabel}>Date et heure de début</span>
          </div>
          <div className={S.dateInputs}>
            <div className={S.inputGroup}>
              <label className={S.label}>Date de début</label>
              <input
                type="date"
                value={frequenceDate}
                onChange={handleDateTimeChange('frequence_date')}
                className={S.dateInput}
              />
            </div>
            <div className={S.inputGroup}>
              <label className={S.label}>Heure de début</label>
              <input
                type="time"
                value={frequenceTime}
                onChange={handleDateTimeChange('frequence_time')}
                className={S.timeInput}
              />
            </div>
          </div>
        </div>

        {/* Date et heure de fin (optionnel) */}
        <div className={S.frequencyGroup}>
          <div className={S.groupHeader}>
            <BiCalendar size={16} />
            <span className={S.groupLabel}>
              Date et heure de fin (optionnel)
            </span>
          </div>
          <div className={S.dateInputs}>
            <div className={S.inputGroup}>
              <label className={S.label}>Date de fin</label>
              <input
                type="date"
                value={endDate}
                onChange={handleDateTimeChange('end_date')}
                className={S.dateInput}
              />
            </div>
            <div className={S.inputGroup}>
              <label className={S.label}>Heure de fin</label>
              <input
                type="time"
                value={endTime}
                onChange={handleDateTimeChange('end_time')}
                className={S.timeInput}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FrequencyInput.propTypes = {
  mode: PropTypes.oneOf(['manual', 'scheduled']).isRequired,
  frequence: PropTypes.string,
  intervalYears: PropTypes.number,
  intervalMonths: PropTypes.number,
  intervalWeeks: PropTypes.number,
  intervalDays: PropTypes.number,
  intervalHours: PropTypes.number,
  intervalMinutes: PropTypes.number,
  intervalSeconds: PropTypes.number,
  frequenceDate: PropTypes.string,
  frequenceTime: PropTypes.string,
  endDate: PropTypes.string,
  endTime: PropTypes.string,
  daysOfWeek: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

FrequencyInput.defaultProps = {
  frequence: '',
  intervalYears: 0,
  intervalMonths: 0,
  intervalWeeks: 0,
  intervalDays: 0,
  intervalHours: 0,
  intervalMinutes: 0,
  intervalSeconds: 0,
  frequenceDate: '',
  frequenceTime: '',
  endDate: '',
  endTime: '',
  daysOfWeek: '',
};

export default React.memo(FrequencyInput);
